import { inject, Injectable } from '@angular/core';
import { FormArray, FormGroup } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { IMenu, ITagWithLabel } from '@tecalisdev/infinity';
import { LanguageService } from '../../core/services/language.service';
import { MenuItemAction } from '../../shared/models/master.models';
import {
	findFormFieldPreset,
	inferFormFieldPresetId,
} from '../components/modal-workflow-form-step/form-field-presets';
import { OPERATION_TYPE } from '../../operations/models/operations.models';
import {
	hasCoincidentFieldWithMatchOcr,
	hasCoincidentFormOcrFields,
	resolveAutofillAfterFormStepsChange,
} from '../utils/form-ocr-autofill.util';
import { patchFormStepToEditor } from '../utils/form-operation-contract.util';
import { formStepListTagsSignature, resolveFormStepListTags } from '../utils/form-step-list-tags.util';
import { WorkflowsHelperService } from './workflows-helper.service';

/**
 * Host para CRUD de pasos FORM del editor (NIF-845).
 */
export interface WorkflowFormStepsHost {
	form: FormGroup;
	modalFormStep: { mode: MenuItemAction; form?: Record<string, unknown>; index?: number } | null;
	readonly formStepsArray: FormArray;
	workflowsHelper: WorkflowsHelperService;
	formStepListTagsCache: Map<number, { signature: string; tags: ITagWithLabel[] }>;
}

/**
 * Pasos FORM del editor: alta/editar/mover/tags (NIF-845).
 *
 * Abrir cuando: `formStepsMenuChange`, tags del listado, sync NIF-657 tras cambios.
 * Mapa: `../WORKFLOWS_EDITOR.md`.
 */
@Injectable({
	providedIn: 'root',
})
export class WorkflowFormStepsService {
	private readonly trans = inject(TranslateService);
	private readonly languageService = inject(LanguageService);

	public removeFormStep(host: WorkflowFormStepsHost, index: number): void {
		const hadCotejo = hasCoincidentFieldWithMatchOcr(
			host.formStepsArray.getRawValue() as Array<Record<string, unknown>>,
		);
		host.formStepsArray.removeAt(index);
		this.renumberFormSteps(host);
		this.invalidateFormStepFieldTagsCache(host);
		this.syncFormOcrOrderPreferenceAfterFormChange(host, hadCotejo);
	}

	public formStep(host: WorkflowFormStepsHost, info: { form: Record<string, unknown>; index?: number } | null): void {
		const hadCotejo = hasCoincidentFieldWithMatchOcr(
			host.formStepsArray.getRawValue() as Array<Record<string, unknown>>,
		);
		if (info?.form) {
			const stepGroup = host.workflowsHelper.createFormStepGroup();
			this.patchFormStepGroup(host, stepGroup, info.form);
			if (info.index !== undefined) {
				host.formStepsArray.setControl(info.index, stepGroup);
			} else {
				host.formStepsArray.push(stepGroup);
			}
			this.invalidateFormStepFieldTagsCache(host);
		}
		host.modalFormStep = null;
		this.syncFormOcrOrderPreferenceAfterFormChange(host, hadCotejo);
	}

	public syncFormOcrOrderPreferenceAfterFormChange(host: WorkflowFormStepsHost, hadCotejo: boolean): void {
		const formAvailable = Boolean(host.form.get(`operations.${OPERATION_TYPE.FORM}.available`)?.value);
		const ocrAvailable = Boolean(host.form.get(`operations.${OPERATION_TYPE.OCR}.available`)?.value);
		const formSteps = host.formStepsArray.getRawValue() as Array<Record<string, unknown>>;
		const autofillControl = host.form.get(`operations.${OPERATION_TYPE.FORM}.autofill_from_ocr`);

		if (!formAvailable || !ocrAvailable) {
			return;
		}

		const nextAutofill = resolveAutofillAfterFormStepsChange({
			hasCoincidentFields: hasCoincidentFormOcrFields(formSteps),
			hadCotejo,
			hasCotejo: hasCoincidentFieldWithMatchOcr(formSteps),
			currentAutofill: autofillControl?.value as boolean | null,
		});
		if (autofillControl && autofillControl.value !== nextAutofill) {
			autofillControl.setValue(nextAutofill);
		}
	}

	public patchFormStepGroup(host: WorkflowFormStepsHost, stepGroup: FormGroup, raw: Record<string, unknown>): void {
		const allowed = this.languageService.getAllowedLanguages();
		const patchedStep = patchFormStepToEditor(raw, allowed);
		stepGroup.patchValue({
			uuid: patchedStep['uuid'],
			step_id: patchedStep['step_id'],
			step_name: patchedStep['step_name'],
			step_description: patchedStep['step_description'],
			alert_msg: patchedStep['alert_msg'],
		});
		const fields = (patchedStep['fields'] as Record<string, unknown>[]) ?? [];
		const fieldsArray = stepGroup.get('fields') as FormArray;
		fields.forEach((field) => {
			const fieldGroup = host.workflowsHelper.createFormFieldGroup();
			fieldGroup.patchValue(host.workflowsHelper.patchFormFieldValue(field));
			((field['options'] as { value: string; label: string }[]) ?? []).forEach((opt) => {
				const optGroup = host.workflowsHelper.formFormFieldOption;
				optGroup.patchValue(opt);
				(fieldGroup.get('options') as FormArray).push(optGroup);
			});
			fieldsArray.push(fieldGroup);
		});
	}

	public newFormStep(host: WorkflowFormStepsHost): void {
		const nextId = host.formStepsArray.length + 1;
		host.modalFormStep = {
			mode: MenuItemAction.CREATE,
			form: { step_id: nextId, step_name: '', fields: [] },
		};
	}

	public formStepsMenuChange(host: WorkflowFormStepsHost, action: IMenu, index: number): void {
		const actionId = action?.id as MenuItemAction | undefined;
		if (!actionId || host.modalFormStep) {
			return;
		}

		const actions: Partial<Record<MenuItemAction, () => void>> = {
			[MenuItemAction.SHOW]: () => {
				host.modalFormStep = {
					mode: MenuItemAction.SHOW,
					form: structuredClone(host.formStepsArray.at(index).getRawValue() as Record<string, unknown>),
					index,
				};
			},
			[MenuItemAction.EDIT]: () => {
				host.modalFormStep = {
					mode: MenuItemAction.EDIT,
					form: structuredClone(host.formStepsArray.at(index).getRawValue() as Record<string, unknown>),
					index,
				};
			},
			[MenuItemAction.DELETE]: () => this.removeFormStep(host, index),
		};
		actions[actionId]?.();
	}

	public onFormStepMove(host: WorkflowFormStepsHost, direction: 'up' | 'down', index: number): void {
		this.moveFormStep(host, index, direction === 'up' ? -1 : 1);
	}

	public moveFormStep(host: WorkflowFormStepsHost, index: number, direction: -1 | 1): void {
		const targetIndex = index + direction;
		if (targetIndex < 0 || targetIndex >= host.formStepsArray.length) {
			return;
		}

		const current = host.formStepsArray.at(index);
		const target = host.formStepsArray.at(targetIndex);
		host.formStepsArray.setControl(index, target);
		host.formStepsArray.setControl(targetIndex, current);
		this.renumberFormSteps(host);
		this.invalidateFormStepFieldTagsCache(host);
	}

	public resolveFormStepPresetTypeLabel(host: WorkflowFormStepsHost, stepIndex: number): string {
		const fields =
			(host.formStepsArray.at(stepIndex).get('fields')?.value as Array<Record<string, unknown>>) ?? [];
		if (!fields.length) {
			return '';
		}

		const presetId = inferFormFieldPresetId(fields[0], fields);
		const preset = findFormFieldPreset(presetId);
		return preset?.labelKey ? this.trans.instant(preset.labelKey) : '';
	}

	public invalidateFormStepFieldTagsCache(host: WorkflowFormStepsHost): void {
		host.formStepListTagsCache.clear();
	}

	public getFormStepListTags(host: WorkflowFormStepsHost, stepIndex: number): ITagWithLabel[] {
		const fields =
			(host.formStepsArray.at(stepIndex).get('fields')?.value as Array<Record<string, unknown>>) ?? [];
		const signature = formStepListTagsSignature(fields);
		const cached = host.formStepListTagsCache.get(stepIndex);
		if (cached?.signature === signature) {
			return cached.tags;
		}

		const tags = resolveFormStepListTags(fields, {
			mandatory: this.trans.instant('WORKFLOWS.FORM.OPERATIONS.MANDATORY'),
			matchOcr: this.trans.instant('WORKFLOWS.FORM.OPERATIONS.MODAL_FORM_STEP.MATCH_OCR_TAG'),
		});

		host.formStepListTagsCache.set(stepIndex, { signature, tags });
		return tags;
	}

	public renumberFormSteps(host: WorkflowFormStepsHost): void {
		host.formStepsArray.controls.forEach((control, index) => {
			control.get('step_id')?.setValue(index + 1);
		});
	}
}
